import React, { useState } from 'react';
import { Icon } from '../../design/Icon';
import { Chip, ClassificationTag, OutcomeBadge, SentimentBadge } from '../../components/shared/Badges';
import { MenuItem, MenuTitle, Popover, usePopover } from '../../components/shared/Overlay';
import { LANGUAGE_OPTIONS, OUTCOME_OPTIONS, SENTIMENT_OPTIONS } from './callFilters';

/** Checkbox list with Clear / Apply (mockup multiPop). `extra` renders above the list and gets the draft state. */
function MultiSelect({ title, options, selected, onApply, extra, initialMatch }) {
  const [picked, setPicked] = useState(selected);
  const [query, setQuery] = useState('');
  const [match, setMatch] = useState(initialMatch);
  const q = query.trim().toLowerCase();
  return (
    <>
      <MenuTitle>{title}</MenuTitle>
      {extra && extra({ query, setQuery, match, setMatch })}
      <div className="tree" style={{ maxHeight: 340 }}>
        {options
          .filter((o) => !q || o.label.toLowerCase().includes(q))
          .map((o) => (
            <label key={o.value} className="tree-row">
              <input
                type="checkbox"
                value={o.value}
                checked={picked.includes(o.value)}
                onChange={(e) => setPicked((cur) => (e.target.checked ? [...cur, o.value] : cur.filter((x) => x !== o.value)))}
              />
              {o.node || o.label}
            </label>
          ))}
      </div>
      <div className="menu-sep" />
      <div className="row between">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setPicked([])}>
          Clear
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onApply(picked, match)}>
          Apply
        </button>
      </div>
    </>
  );
}

function FilterChip({ id, keyLabel, value, active, width = 330, align, children }) {
  const pop = usePopover();
  return (
    <Popover
      open={pop.open}
      onClose={pop.close}
      width={width}
      align={align}
      label={keyLabel}
      anchor={
        <Chip id={id} keyLabel={keyLabel} active={Boolean(active)} aria-haspopup="true" aria-expanded={pop.open} onClick={pop.toggle}>
          {value}
          <Icon name="chevD" className="ico-sm" />
        </Chip>
      }
    >
      {pop.open && children(pop.close)}
    </Popover>
  );
}

/** Page filters of the call log (mockup template #page-filters): classification, sentiment, language, transcript, outcome. */
export function RemovableChip({ id, keyLabel, value, clearLabel, onClear }) {
  return (
    <span className="chip active" id={id}>
      <span className="k">{keyLabel}</span> {value}
      <button type="button" className="x" aria-label={clearLabel} onClick={onClear}>
        <Icon name="x" className="ico-sm" />
      </button>
    </span>
  );
}

export function CallFilterChips({ filters, categories, agentLabel, queueLabel, onChange }) {
  const names = filters.cls.map((id) => categories.find((c) => c.id === id)?.name || 'Unknown classification');
  const clsValue = filters.cls.length === 0 ? 'Any' : filters.cls.length === 1 ? names[0] : `${filters.cls.length} (${filters.match})`;

  return (
    <>
      <span className="sep" aria-hidden="true" />
      {filters.q && <RemovableChip id="f-search" keyLabel="Search" value={filters.q} clearLabel="Clear search" onClear={() => onChange({ q: '' })} />}
      {filters.queueIds.length > 0 && (
        <RemovableChip id="f-queue" keyLabel="Queue" value={queueLabel} clearLabel="Clear queue filter" onClear={() => onChange({ queueIds: [], queueName: '' })} />
      )}
      {filters.agentIds.length > 0 && (
        <RemovableChip id="f-agent" keyLabel="Agent" value={agentLabel} clearLabel="Clear agent filter" onClear={() => onChange({ agentIds: [], agentName: '' })} />
      )}
      <FilterChip id="f-cls" keyLabel="Classification" value={clsValue} active={filters.cls.length}>
        {(close) => (
          <MultiSelect
            title="Classification"
            selected={filters.cls}
            initialMatch={filters.match}
            options={categories.map((c) => ({
              value: c.id,
              label: c.name,
              node: (
                <>
                  <span className="grow">{c.name}</span>
                  <span className="cnt">{c.subcategories && c.subcategories.length ? '' : 'call-level'}</span>
                </>
              ),
            }))}
            extra={({ query, setQuery, match, setMatch }) => (
              <>
                <input className="input" placeholder="Search classifications" aria-label="Search classifications" style={{ marginBottom: 6 }} value={query} onChange={(e) => setQuery(e.target.value)} />
                <div className="row small" style={{ gap: 14, padding: '4px 8px 8px' }}>
                  <label className="check">
                    <input type="radio" name="match" value="any" checked={match === 'any'} onChange={() => setMatch('any')} />
                    Match any
                  </label>
                  <label className="check">
                    <input type="radio" name="match" value="all" checked={match === 'all'} onChange={() => setMatch('all')} />
                    Match all
                  </label>
                </div>
              </>
            )}
            onApply={(picked, match) => {
              onChange({ cls: picked, match });
              close();
            }}
          />
        )}
      </FilterChip>
      <FilterChip id="f-sent" keyLabel="Sentiment" value={filters.sent.length ? filters.sent.join(', ') : 'Any'} active={filters.sent.length}>
        {(close) => (
          <MultiSelect
            title="Caller sentiment"
            selected={filters.sent}
            options={SENTIMENT_OPTIONS.map((v) => ({ value: v, label: v, node: <SentimentBadge sentiment={v} /> }))}
            onApply={(picked) => {
              onChange({ sent: picked });
              close();
            }}
          />
        )}
      </FilterChip>
      <FilterChip id="f-lang" keyLabel="Language" value={filters.lang || 'Any'} active={filters.lang} width={220}>
        {(close) => (
          <>
            <MenuTitle>Language detected</MenuTitle>
            {LANGUAGE_OPTIONS.map(([value, label]) => (
              <MenuItem
                key={value || 'any'}
                checkable
                on={filters.lang === value}
                onClick={() => {
                  onChange({ lang: value });
                  close();
                }}
              >
                {label}
              </MenuItem>
            ))}
          </>
        )}
      </FilterChip>
      <Chip id="f-tr" active={filters.tr} aria-pressed={filters.tr ? 'true' : 'false'} onClick={() => onChange({ tr: !filters.tr })}>
        <Icon name={filters.tr ? 'check' : 'file'} className="ico-sm" />
        Has transcript
      </Chip>
      <FilterChip id="f-out" keyLabel="Outcome" value={filters.out.length ? filters.out.join(', ') : 'Any'} active={filters.out.length}>
        {(close) => (
          <MultiSelect
            title="Outcome"
            selected={filters.out}
            options={OUTCOME_OPTIONS.map((v) => ({ value: v, label: v, node: <OutcomeBadge outcome={v} /> }))}
            onApply={(picked) => {
              onChange({ out: picked });
              close();
            }}
          />
        )}
      </FilterChip>
    </>
  );
}

export function ClassificationCell({ call }) {
  const cls = call.classifications || [];
  if (!cls.length) {
    const status = call.contentDeletedAt ? 'deleted' : call.analysisStatus;
    const text = {
      Pending: 'Analyzing',
      NotApplicable: 'No recording',
      Failed: 'Analysis failed',
      Completed: 'No classifications',
      deleted: 'Content deleted',
    }[status] || 'Not analyzed';
    return (
      <span className="caption">
        {status === 'Pending' && (
          <>
            <Icon name="clock" className="ico-sm" />{' '}
          </>
        )}
        {text}
      </span>
    );
  }
  return (
    <>
      {cls.slice(0, 2).map((c, i) => (
        <ClassificationTag key={`${c.categoryName}-${i}`} category={c.categoryName} subcategory={c.subcategoryName} severity={c.severity} />
      ))}
      {cls.length > 2 && <span className="tag-more">+{cls.length - 2}</span>}
    </>
  );
}
